import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ClipboardList, Plus, Search, MoreVertical, Copy, Trash2, Pencil, ListChecks, Upload, Layers } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Consultation", "Disease", "Condition", "Treatment", "Procedure", "Clinical Category", "Custom"];

export default function ClinicalForms() {
  const navigate = useNavigate();
  const [forms, setForms] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/forms").then((r) => setForms(r.data)).catch(() => setForms([]));
  const loadConditions = () => api.get("/conditions").then((r) => setConditions(r.data)).catch(() => {});
  useEffect(() => { load(); loadConditions(); }, []);
  const token = typeof window !== "undefined" ? localStorage.getItem("contaha_token") : "";
  const condImg = (cd) => cd.image_url ? (cd.image_url.startsWith("http") ? cd.image_url : `${API}/files/${cd.image_url}?auth=${token}`) : null;
  const uploadCondImg = async (cid, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/conditions/${cid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Image updated"); loadConditions(); }
    catch { toast.error("Upload failed (admin only)"); }
  };

  const create = async () => {
    const { data } = await api.post("/forms", { title: "Untitled Form", category: "Condition", condition_slug: "implant", status: "draft", questions: [] });
    navigate(`/forms/${data.id}`);
  };
  const setDefault = async (id) => { await api.post(`/forms/${id}/set-default`); toast.success("Set as default"); load(); };
  const duplicate = async (id) => { await api.post(`/forms/${id}/duplicate`); toast.success("Form duplicated"); load(); };
  const remove = async (id) => { await api.delete(`/forms/${id}`); toast.success("Form archived"); load(); };
  const toggleStatus = async (f) => { await api.put(`/forms/${f.id}`, { status: f.status === "active" ? "draft" : "active" }); toast.success("Status updated"); load(); };

  const filtered = (forms || []).filter((f) => {
    const matchQ = f.title.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || f.status === filter;
    return matchQ && matchF;
  });

  return (
    <div>
      <PageHeader title="Clinical Forms" subtitle="Build dynamic questionnaires for your practice" breadcrumb={["Clinical", "Clinical Forms"]}
        actions={<Button onClick={create} data-testid="create-form-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><Plus className="h-4 w-4 mr-1.5" />Create Form</Button>} />

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Conditions</h3><span className="text-xs text-[#8B9698]">— category images shown on the consultation entrance</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="conditions-manager">
          {conditions.map((cd) => (
            <SurfaceCard key={cd.id} className="overflow-hidden p-0">
              <div className="relative h-24 bg-[#F4FBF7] dark:bg-[#0E1A21]">
                {condImg(cd) ? <img src={condImg(cd)} alt={cd.name} className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ background: cd.color }} />}
                <label className="absolute bottom-1.5 right-1.5 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg bg-black/60 text-white hover:bg-black/80" data-testid={`cond-upload-${cd.slug}`}>
                  <Upload className="h-3.5 w-3.5" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCondImg(cd.id, e.target.files?.[0])} />
                </label>
              </div>
              <div className="p-2.5"><div className="text-xs font-bold text-[#101516] dark:text-white flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: cd.color }} />{cd.name}</div>{cd.is_default && <span className="text-[10px] text-[#8B9698]">Default condition</span>}</div>
            </SurfaceCard>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B9698]" />
          <Input data-testid="form-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search forms..." className="pl-9 h-10" />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList><TabsTrigger value="all">All</TabsTrigger><TabsTrigger value="active">Active</TabsTrigger><TabsTrigger value="draft">Draft</TabsTrigger></TabsList>
        </Tabs>
      </div>

      {forms && filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No clinical forms" description="Create your first clinical form. Build dynamic questionnaires for your practice." actionLabel="Create Clinical Form" onAction={create} testId="forms-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((f) => (
            <SurfaceCard key={f.id} className="p-5 group">
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15"><ListChecks className="h-5 w-5 text-[#00A85A]" /></div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button data-testid={`form-menu-${f.id}`} className="text-[#8B9698] hover:text-[#101516] dark:hover:text-white p-1"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/forms/${f.id}`)}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggleStatus(f)}><ListChecks className="h-4 w-4 mr-2" />{f.status === "active" ? "Set to Draft" : "Activate"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDefault(f.id)}><ListChecks className="h-4 w-4 mr-2" />Set as default</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicate(f.id)}><Copy className="h-4 w-4 mr-2" />Duplicate</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(f.id)} className="text-red-500 focus:text-red-500"><Trash2 className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <button onClick={() => navigate(`/forms/${f.id}`)} data-testid={`form-card-${f.id}`} className="mt-3 block w-full text-left">
                <div className="text-sm font-bold text-[#101516] dark:text-white">{f.title}</div>
                <div className="text-xs text-[#8B9698] mt-0.5 line-clamp-1">{f.description || "No description"}</div>
              </button>
              <div className="mt-4 flex items-center justify-between border-t border-[#E2E8F0]/60 dark:border-[#223139]/60 pt-3">
                <div className="flex items-center gap-2 flex-wrap"><span className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{f.condition_slug || f.category}</span><span className="text-[11px] text-[#8B9698]">· {(f.questions || []).length} questions</span>{f.is_default && <span className="rounded bg-[#00A85A]/15 px-1.5 py-0.5 text-[9px] font-bold text-[#008F4D] dark:text-[#3FD98A]">DEFAULT</span>}</div>
                <StatusBadge status={f.status} />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
